'use client'

import { useState } from 'react'
import Link from 'next/link'
import { useAuth, tierPricing } from '@/contexts/AuthContext'

export default function SettingsPage() {
  const { user, tierLabel, logout, alertsRemaining, watchlistLimit, canExport } = useAuth()
  const [notifications, setNotifications] = useState({
    email: true,
    alerts: true,
    newsletter: false,
    marketing: false,
  })

  if (!user) {
    return (
      <div className="px-4 md:px-6 py-6">
        <div className="max-w-lg mx-auto text-center py-12">
          <h1 className="font-display text-2xl font-light mb-4">Sign in to access settings</h1>
          <Link
            href="/get-started"
            className="pill"
            style={{ background: 'var(--accent-primary)', color: 'white' }}
          >
            Get started
          </Link>
        </div>
      </div>
    )
  }

  const tiers = [
    { id: 'individual', name: 'Individual', price: '$199/mo', features: ['Full auction history (50+ years)', 'AI-powered market analysis', 'Unlimited artist tracking', 'CSV/PDF exports'] },
    { id: 'institutional', name: 'Institutional', price: 'Custom', features: ['Everything in Individual', 'API & MCP access', 'Multi-seat team access', 'Dedicated support'] },
  ]

  return (
    <div className="px-4 md:px-6 py-6">
      <h1 className="font-display text-2xl md:text-3xl font-light mb-6">Settings</h1>

      <div className="max-w-2xl space-y-8">
        {/* Account section */}
        <section className="card p-6">
          <h2 className="font-medium mb-4">Account</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-border-subtle">
              <div>
                <p className="text-sm font-medium">Name</p>
                <p className="text-text-tertiary text-sm">{user.name}</p>
              </div>
              <button className="text-sm text-accent-primary hover:underline">Edit</button>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-border-subtle">
              <div>
                <p className="text-sm font-medium">Email</p>
                <p className="text-text-tertiary text-sm">{user.email}</p>
              </div>
              <button className="text-sm text-accent-primary hover:underline">Change</button>
            </div>
            <div className="flex items-center justify-between py-3">
              <div>
                <p className="text-sm font-medium">Password</p>
                <p className="text-text-tertiary text-sm">••••••••</p>
              </div>
              <button className="text-sm text-accent-primary hover:underline">Update</button>
            </div>
          </div>
        </section>

        {/* Subscription section */}
        <section className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-medium">Subscription</h2>
            <span
              className="text-xs px-3 py-1 rounded-full font-medium"
              style={{ background: 'var(--accent-primary)', color: 'white' }}
            >
              {tierLabel} Plan
            </span>
          </div>

          {/* Current plan details */}
          <div className="bg-page rounded-xl p-4 mb-4">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-display font-light">
                  {watchlistLimit === Infinity ? '∞' : watchlistLimit}
                </p>
                <p className="text-xs text-text-tertiary">Watchlist limit</p>
              </div>
              <div>
                <p className="text-2xl font-display font-light">{alertsRemaining}</p>
                <p className="text-xs text-text-tertiary">Alerts remaining</p>
              </div>
              <div>
                <p className="text-2xl font-display font-light">{canExport ? 'Yes' : 'No'}</p>
                <p className="text-xs text-text-tertiary">Export access</p>
              </div>
            </div>
          </div>

          {/* Plan options */}
          {user.tier !== 'institutional' && (
            <div className="space-y-3">
              <p className="text-sm text-text-secondary">Available plans</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {tiers
                  .filter((t) => t.id !== user.tier)
                  .map((tier) => (
                    <Link
                      key={tier.id}
                      href="/get-started"
                      className="p-4 rounded-xl border border-border hover:border-accent-primary transition-all group"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium group-hover:text-accent-primary transition-colors">
                          {tier.name}
                        </span>
                        <span className="text-sm text-text-tertiary">{tier.price}</span>
                      </div>
                      <ul className="space-y-1">
                        {tier.features.slice(0, 2).map((feature) => (
                          <li key={feature} className="text-xs text-text-tertiary flex items-center gap-1">
                            <svg className="w-3 h-3 text-positive" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </Link>
                  ))}
              </div>
            </div>
          )}
        </section>

        {/* Notifications section */}
        <section className="card p-6">
          <h2 className="font-medium mb-4">Notifications</h2>
          <div className="space-y-4">
            {[
              { key: 'email', label: 'Email notifications', description: 'Receive important updates via email' },
              { key: 'alerts', label: 'Price alerts', description: 'Get notified when your alerts trigger' },
              { key: 'newsletter', label: 'Weekly newsletter', description: 'Market insights and trending artists' },
              { key: 'marketing', label: 'Marketing emails', description: 'Product updates and special offers' },
            ].map((item) => (
              <div key={item.key} className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm font-medium">{item.label}</p>
                  <p className="text-xs text-text-tertiary">{item.description}</p>
                </div>
                <button
                  onClick={() => setNotifications((prev) => ({ ...prev, [item.key]: !prev[item.key as keyof typeof prev] }))}
                  className={`w-12 h-6 rounded-full transition-all ${
                    notifications[item.key as keyof typeof notifications]
                      ? 'bg-accent-primary'
                      : 'bg-border'
                  }`}
                >
                  <div
                    className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                      notifications[item.key as keyof typeof notifications]
                        ? 'translate-x-6'
                        : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Danger zone */}
        <section className="card p-6 border-negative/30">
          <h2 className="font-medium text-negative mb-4">Danger Zone</h2>
          <div className="space-y-3">
            <button
              onClick={logout}
              className="w-full py-3 rounded-xl border border-border text-text-secondary hover:border-negative hover:text-negative transition-all text-sm font-medium"
            >
              Sign out
            </button>
            <button className="w-full py-3 rounded-xl border border-negative/30 text-negative hover:bg-negative/5 transition-all text-sm font-medium">
              Delete account
            </button>
          </div>
        </section>
      </div>
    </div>
  )
}
